import { useEffect, useRef, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components"
import { cn } from "@/lib/utils"
import { Chunkify } from "../../components-legacy/Chunkify.js"
import { textToHtml } from "../../components-legacy/Formatter.js"
import Loader from "../../components-legacy/Loader.jsx"
import { StreamFormatter } from "../../components-legacy/StreamFormatter.js"
import { UpdateTitle } from "../../components-legacy/UpdateTitle.jsx"
import { getBacktestProcessingStatus } from "../../system/util.js"
import { server } from "../chat/Server.js"
import { useSSE } from "../../contexts/SSEContext.jsx"
// import StrategyBacktest from "./StrategyBacktest.jsx"
import StrategyCode from "./StrategyCode.jsx"
import StrategyResults from "./StrategyResults.jsx"
import BuyCreditsMessage from "../../components-legacy/BuyCreditsMessage.jsx"
import WarningIcon from '../../assets/svg/warning.svg?react'
import "./archived-strategy.css"

export default function StrategyAI({ sid, data }) {
    const { tab, bid } = useParams()
    const [loaded, setLoaded] = useState(false)
    const [connected, setConnected] = useState(false)
    const [error, setError] = useState(null)

    const [loadedCode, setLoadedCode] = useState(false)
    const [hasCode, setHasCode] = useState(null)
    const [codeContent, setCodeContent] = useState('')
    const [codeState, setCodeState] = useState()
    const [errorCode, setErrorCode] = useState(null)

    const [backtest, setBacktest] = useState(false)
    const [lastBacktestMessage, setLastBacktestMessage] = useState(null)

    const refOutline = useRef(null)
    const refMessages = useRef(null)
    const navigate = useNavigate()

    useEffect(() => {
        setConnected(true)

        let chunkifyOutline, chunkifyMessages, chunkifyCode
        const formatterOutline = new StreamFormatter()
        refOutline.current.appendChild(formatterOutline.root)

        const formatterMessages = new StreamFormatter()
        refMessages.current.appendChild(formatterMessages.root)

        // Store formatters and chunkify instances in refs so they can be accessed in the SSE handler
        const formattersRef = {
            formatterOutline,
            formatterMessages,
            chunkifyOutline,
            chunkifyMessages,
            chunkifyCode
        }

        // Store the formatters in a way that the SSE handler can access them
        window.__strategyFormatters = formattersRef

        return () => {
            setConnected(false)
            chunkifyOutline?.stop()
            chunkifyMessages?.stop()
            chunkifyCode?.stop()
            delete window.__strategyFormatters
        }

    }, [sid])

    // Handle SSE messages
    const handleSSEMessage = useCallback((message) => {
        console.debug('📧 MESSAGE RECEIVED', message)

        const formatters = window.__strategyFormatters
        if (!formatters) return

        const { formatterOutline, formatterMessages } = formatters

        if (message.event === 'strategy-message-user') {
            const el = document.createElement('p')
            el.className = 'usr'
            el.textContent = message.message
            formatterMessages.root.appendChild(el)
            // formatterMessages.root.scrollIntoView({ block: 'end' })
        }

        if (message.event === 'strategy-message-agent') {

            const msg = message.message

            let assistant = ''

            setHasCode(false)

            if (msg.clarifications) {
                assistant += msg.clarifications + "\n"
            }

            if (msg.followUpQuestion) {
                assistant += '### ' + msg.followUpQuestion + "\n"
            }

            if (!assistant) {
                assistant = 'Do you have any specific requirements for the strategy?'
            }

            if (message.stream) {
                const chunkifyMessages = Chunkify(assistant, {
                    callback: chunk => {
                        // formatterMessages.root.scrollIntoView({ block: 'end' })
                        formatterMessages.addChunk(chunk)
                    },
                    maxLat: 50,
                    maxChunk: 20
                })
                chunkifyMessages.run()
                formatters.chunkifyMessages = chunkifyMessages
            } else {
                formatterMessages.addChunk(assistant || '')
            }
        }

        if (message.event === 'strategy-message-list') {

            if (message.messages && message.messages.length > 0) {
                for (let i = 0; i < message.messages.length; i++) {
                    const msg = message.messages[i]

                    if (msg.user) {
                        const el = document.createElement('p')
                        el.className = 'usr'
                        el.textContent = msg.user
                        formatterMessages.root.appendChild(el)
                    }

                    if (msg.assistant?.clarifications) {
                        formatterMessages.addChunk(msg.assistant?.clarifications + "\n")
                    }

                    if (msg.assistant?.followUpQuestion) {
                        formatterMessages.addChunk('### ' + msg.assistant?.followUpQuestion + "\n")
                    }

                    if (!msg.assistant?.clarifications && !msg.assistant?.followUpQuestion) {
                        formatterMessages.addChunk("Do you have any specific requirements for the strategy?\n")
                    }
                }

                // formatterMessages.root.scrollIntoView({ block: 'end' })
            }
            setHasCode(message.hasCode === true)
        }

        if (message.event === 'strategy-outline') {
            formatterOutline.root.textContent = ''
            if (message.stream) {
                const chunkifyOutline = Chunkify(message.outline, {
                    callback: chunk => {
                        formatterOutline.addChunk(chunk)
                    },
                    maxLat: 100,
                    maxChunk: 50
                })
                chunkifyOutline.run()
                formatters.chunkifyOutline = chunkifyOutline
            } else {
                refOutline.current.innerHTML = textToHtml(message.outline || '')
            }
        }

        if (message.event === 'strategy-code') {
            setCodeContent('')
            setCodeContent(message.code || '')
            setCodeState(message.state)
        }

        if (message.event === 'strategy-code-state') {
            setCodeState(message.state)
        }

        if (message.event === 'strategy-backtest-processing') {
            setLastBacktestMessage({ btId: message.btId, strId: message.strId, processing: message.processing, isMessage: true })
        }

    }, [])

    // Subscribe to SSE messages for this strategy
    useSSE(
        useCallback((msg) => msg.strId == sid, [sid]),
        handleSSEMessage
    )

    useEffect(() => {
        if (!connected) {
            return
        }
        server.api(`strategy/get`, { strId: sid }).then(() => {
            setLoaded(true)
        }).catch(err => {
            setLoaded(true)
            if (err.errorCode === 'SUBSCRIPTION_PENDING') {
                setError(
                    <div className="rounded-md border border-warning-dim bg-warning-dim text-foreground text-body p-4 flex flex-wrap items-center gap-2">
                        <span>Subscription is pending. Please check your account to proceed.</span>
                        <Button size="sm" className="text-label" onClick={() => navigate('/billing')}>
                            Billing
                        </Button>
                    </div>
                )
            } else if (err.errorCode === 'INSUFFICIENT_CREDITS') {
                setError(<BuyCreditsMessage />)
            } else {
                setError(
                    <div className="rounded-md border border-danger-dim bg-danger-dim text-danger text-body px-4 py-2">
                        An error occurred. Please try again.
                    </div>
                )
            }
        })
    }, [connected, sid])

    useEffect(() => {
        if (tab !== 'code' || hasCode === null || loadedCode) {
            return
        }
        setLoadedCode(true)
        setErrorCode(null)
        server.api(`strategy/code`, { strId: sid }).then(() => {
            setHasCode(true)
        }).catch(err => {
            setErrorCode(err.errorCode)
        })

    }, [tab, hasCode, sid, loadedCode])

    useEffect(() => {
        if (tab !== 'results') {
            return
        }

        server.api('strategy/backtest', { strId: sid, backtestId: bid }).then(data => {
            setBacktest({ ...data, processingStatus: getBacktestProcessingStatus(data.processing) })
            setLastBacktestMessage(null)
        })
            .catch(() => {
                setBacktest(null)
            })

    }, [tab, sid, bid])

    useEffect(() => {
        if (!lastBacktestMessage || !backtest) {
            return
        }

        if (lastBacktestMessage.strId == sid && lastBacktestMessage.processing != backtest.processing) {
            if (lastBacktestMessage.btId == backtest.backtestId) {
                setBacktest(backtest => {
                    return {
                        ...backtest,
                        processing: lastBacktestMessage.processing,
                        processingStatus: getBacktestProcessingStatus(lastBacktestMessage.processing),
                        isMessage: lastBacktestMessage.isMessage
                    }
                })
            }
        }

    }, [lastBacktestMessage, backtest, bid, sid])



    const showLoader = !loaded && !error

    return (
        <div className="strategy-ai flex flex-col h-full bg-background text-foreground">
            <div className="w-full border-b border-border bg-card">
                <div className="mx-auto max-w-3xl w-full p-4">

                    <div className="mb-3 text-body">
                        <UpdateTitle strId={sid} initialTitle={data.title} hasCode={hasCode} strType={data.type} />
                    </div>
                    <div className="flex items-center gap-2">
                        <BtnMenu slug="strategy" tab={tab} sid={sid} navigate={navigate}>
                            Strategy
                        </BtnMenu>
                        <BtnMenu slug="chat" tab={tab} sid={sid} navigate={navigate}>
                            Chat
                        </BtnMenu>
                        {hasCode !== false ? <BtnMenu slug="code" tab={tab} sid={sid} navigate={navigate} isDisabled={!loaded || hasCode === null}>Code</BtnMenu> : null}
                        {/* <BtnMenu slug="backtest" tab={tab} sid={sid} navigate={navigate} isDisabled={!loaded || hasCode === null}>
                                Backtest
                            </BtnMenu>  */}
                        <BtnMenu slug="results" tab={tab} sid={sid} navigate={navigate} isDisabled={!loaded || hasCode === null}>
                            Backtest
                        </BtnMenu>
                    </div>
                </div>
            </div>

            <div className="main flex-1 overflow-y-auto">

                {/* Strategy Tab */}
                <div className={cn("mx-auto max-w-3xl w-full px-4 pt-4", tab === 'strategy' ? 'block' : 'hidden')}>
                    <div className="p-4 rounded-md mb-4 flex gap-2 items-start border border-warning-dim bg-warning-dim text-foreground text-body">
                        <WarningIcon className="text-warning shrink-0 mt-0.5" />
                        <span>The bots based on MQL are deprecated. You can still visualize them, but editing or new backtests are disabled. Please use new bots based on Python instead.</span>
                    </div>

                    <div className="strategy-ai-chat-outline px-4 sm:px-8 rounded-md bg-card text-foreground">
                        {showLoader && <Loader />}

                        {error !== null ? (
                            error
                        ) : (
                            <div className="strategy-summary overflow-hidden" >
                                <div className="strategy-summary-inner h-full overflow-y-auto" ref={refOutline}> </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Chat Tab */}
                <div className={cn("mx-auto max-w-3xl w-full px-4 pt-4", tab === 'chat' ? 'block' : 'hidden')}>
                    <div className="strategy-ai-chat-chat px-4 sm:px-8 rounded-md w-full bg-card text-foreground">
                        <div ref={refMessages} className="chat-msg" ></div>
                    </div>
                </div>

                {/* Strategy Code */}
                <div className={cn("mx-auto max-w-3xl w-full pt-4", tab === 'code' ? 'block' : 'hidden')}>
                    <StrategyCode strId={sid} error={errorCode} code={codeContent} state={codeState} setCodeState={setCodeState} strategyType={data.type} />
                </div>

                {/* {tab === 'backtest' && (
                            <div className="flex pb-20">
                                <div className="strategy-ai-chat-backtest">
                                    <div>
                                        {loaded && hasCode ? (
                                            <StrategyBacktest />
                                        ) : (
                                            <section className="p-6 rounded-md min-w-[320px] border border-border bg-card text-foreground">
                                                Algo code not found
                                            </section>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )} */}

                {tab === 'results' && (
                    <div className="mx-auto max-w-3xl w-full pb-4 px-4">
                        <div className="strategy-ai-chat-results">
                            <div className="content mt-4 ">
                                {loaded && hasCode ? (
                                    <StrategyResults sid={sid} backtest={backtest} />
                                ) : (
                                    <section className="p-6 rounded-md min-w-[320px] border border-border bg-card text-foreground">
                                        Algo code not found
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>
                )}


            </div>
        </div>
    )

}

function BtnMenu({ slug, tab, sid, navigate, isDisabled, children }) {
    const isActive = tab === slug
    return (
        <button
            type="button"
            className={cn(
                "px-3 py-1.5 rounded-md text-label transition-colors cursor-pointer",
                isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-hover",
                isDisabled && "opacity-50 cursor-not-allowed"
            )}
            disabled={isDisabled}
            onClick={() => navigate(`/archived/strategy/${sid}/${slug}`)}
        >
            {children}
        </button>
    )
}
